import React, {useState, useEffect, useMemo} from "react";
import {hasAuthParams, useAuth} from "react-oidc-context";
import {db} from "./config/firebase-config";
import useAuthorization from "./auth/useAuthorization";
import {
  FEHLER_KEIN_ZUGRIFF,
  FEHLER_NETZ,
  FEHLER_SITZUNG_ABGELAUFEN,
  FEHLER_TOKEN_URL_FEHLT,
  ZUSTAND_BEREIT,
  ZUSTAND_FEHLER,
  ZUSTAND_TAUSCHEND,
  useFirebaseIdentitaet,
} from "./auth/FirebaseIdentitaet";

import {
  collection,
  collectionGroup,
  onSnapshot,
  query,
  orderBy,
} from "firebase/firestore";

import Login from "./components/Login";
import Overview from "./components/Overview";
import Header from "./components/Header";
import Einstellungen from "./components/Einstellungen";
import Protokoll from "./components/Protokoll";
import Changelog from "./components/Changelog";
import Fahrzeug from "./components/Fahrzeug";
import ProtokollFahrzeug from "./components/ProtokollFahrzeug";
import Maengel from "./components/Maengel";
import {ladefehlerText, neueMaengelAnzahl} from "./utils/maengel";

const fahrzeugeRef = collection(db, "fahrzeuge");
const funkkennungenRef = collection(db, "funkkennungen");
const kategorienRef = collection(db, "kategorien");
// Mängel liegen als Subcollection unter dem jeweiligen Fahrzeug — die
// Gesamtsicht entsteht nur über eine Collection-Group-Abfrage. Sie setzt eine
// eigene Security Rule auf dem Gruppenpfad und einen Collection-Group-Index
// voraus; beides liegt in firestore.rules/firestore.indexes.json, siehe
// docs/firestore-rules.md.
const maengelRef = collectionGroup(db, "maengel");

// Ein permission-denied wird angezeigt, nicht mit einem neuen Tausch
// beantwortet: Passt die Identität, liegt es an den Regeln, und ein Tausch
// liefe nur in eine Schleife.
const listenLadefehler = (liste, error) =>
  error?.code === "permission-denied"
    ? `Keine Leseberechtigung für die ${liste}. Bitte die Firestore-Regeln prüfen.`
    : `${liste} konnten nicht geladen werden.`;

const App = () => {
  const auth = useAuth();
  const {hasAppAccess} = useAuthorization();
  const identitaet = useFirebaseIdentitaet();
  const bereit = identitaet.zustand === ZUSTAND_BEREIT;
  const [page, setPage] = useState("overview");
  const [pageKennzeichen, setPageKennzeichen] = useState("testkennzeichen ");

  const [fahrzeuge, setFahrzeuge] = useState([]);
  const [funkkennungen, setFunkkennungen] = useState([]);
  const [kategorien, setKategorien] = useState([]);
  const [maengel, setMaengel] = useState([]);
  const [maengelLaden, setMaengelLaden] = useState(true);
  const [maengelFehler, setMaengelFehler] = useState("");
  const [ladefehler, setLadefehler] = useState({});
  const [searchString, setSearchString] = useState("");
  const [fahrzeugId, setFahrzeugId] = useState("");

  // Der Zähler am Menüpunkt „Mängel" hängt am selben Live-Listener wie die
  // Liste und steht darum hier, nicht in der Mängelseite: er soll auch dann
  // stimmen, wenn diese Seite gar nicht offen ist.
  const neueMaengel = useMemo(() => {
    return neueMaengelAnzahl(maengel, fahrzeuge);
  }, [maengel, fahrzeuge]);

  const changePage = (newPage) => {
    setPage(newPage);
  };

  const gotoFahrzeugPage = (kennzeichen) => {
    setPageKennzeichen(kennzeichen);
    setPage("fahrzeug");
  };

  // kategorien braucht auch der öffentliche Meldeweg, der Listener läuft
  // darum immer.
  useEffect(() => {
    const queryKategorien = query(kategorienRef, orderBy("bezeichnung"));

    return onSnapshot(queryKategorien, (snapshot) => {
      let kategorien = [];
      snapshot.forEach((doc) => {
        kategorien.push({...doc.data(), id: doc.id});
      });
      setKategorien(kategorien);
    });
  }, []);

  // Interne Daten liest Firestore nur mit passender Firebase-Identität. Ohne
  // sie liefen die Listener in permission-denied; beim Verlassen von „bereit"
  // wird darum abgemeldet und der State geleert, damit keine Daten eines
  // Vorgängers stehen bleiben.
  useEffect(() => {
    if (!bereit) {
      return;
    }

    const queryFahrzeuge = query(fahrzeugeRef, orderBy("funkkennung"));

    const ladefehlerMelden = (liste) => (error) => {
      console.error(`${liste} konnten nicht geladen werden`, error);
      setLadefehler((alt) => ({
        ...alt,
        [liste]: listenLadefehler(liste, error),
      }));
    };

    const unsubscribe = onSnapshot(
      queryFahrzeuge,
      (snapshot) => {
        let fahrzeugeTmp = [];
        snapshot.forEach((doc) => {
          fahrzeugeTmp.push({...doc.data(), id: doc.id});
        });
        setFahrzeuge(fahrzeugeTmp);
      },
      ladefehlerMelden("Fahrzeuge"),
    );

    const queryFunkkennungen = query(funkkennungenRef, orderBy("bezeichnung"));

    const unsubscribe2 = onSnapshot(
      queryFunkkennungen,
      (snapshot) => {
        let funkkennungen = [];
        snapshot.forEach((doc) => {
          funkkennungen.push({...doc.data(), id: doc.id});
        });
        setFunkkennungen(funkkennungen);
      },
      ladefehlerMelden("Funkkennungen"),
    );

    const queryMaengel = query(maengelRef, orderBy("datumAngelegt", "desc"));

    const unsubscribe3 = onSnapshot(
      queryMaengel,
      (snapshot) => {
        let maengelTmp = [];
        snapshot.forEach((doc) => {
          // Der Fahrzeugbezug steht nicht im Dokument, sondern nur im Pfad.
          // datumAngelegt setzt der Server; bis er bestätigt, gilt die
          // Schätzung statt null.
          maengelTmp.push({
            ...doc.data({serverTimestamps: "estimate"}),
            id: doc.id,
            fahrzeugId: doc.ref.parent.parent?.id,
          });
        });
        setMaengel(maengelTmp);
        setMaengelFehler("");
        setMaengelLaden(false);
      },
      (error) => {
        // Sobald dieser Callback existiert, gibt das Firestore-SDK den Fehler
        // nicht mehr selbst aus — und damit auch nicht mehr den Link, mit dem
        // sich ein fehlender Index in der Konsole vorausgefüllt anlegen lässt.
        console.error("Mängel konnten nicht geladen werden", error);
        // Ohne Regel bzw. Index bliebe die Seite sonst wortlos leer.
        setMaengelFehler(ladefehlerText(error));
        setMaengel([]);
        setMaengelLaden(false);
      },
    );

    return () => {
      unsubscribe();
      unsubscribe2();
      unsubscribe3();
      setFahrzeuge([]);
      setFunkkennungen([]);
      setMaengel([]);
      setMaengelFehler("");
      setMaengelLaden(true);
      setLadefehler({});
    };
  }, [bereit]);

  const warten = <div className="login">Anmeldung wird verarbeitet …</div>;

  if (
    auth.isLoading ||
    auth.activeNavigator === "signinRedirect" ||
    hasAuthParams()
  ) {
    return warten;
  }

  if (!auth.isAuthenticated) {
    return <Login kategorien={kategorien} accessDenied={false} />;
  } else if (identitaet.zustand === ZUSTAND_TAUSCHEND) {
    return warten;
  } else if (
    identitaet.zustand === ZUSTAND_FEHLER &&
    identitaet.fehler !== FEHLER_KEIN_ZUGRIFF
  ) {
    return <AnmeldeFehler identitaet={identitaet} />;
  } else if (!bereit || !hasAppAccess) {
    return <Login kategorien={kategorien} accessDenied={true} />;
  } else {
    return (
      <>
        <Header
          page={page}
          changePage={changePage}
          searchString={searchString}
          setSearchString={setSearchString}
          neueMaengel={neueMaengel}
        />
        {Object.values(ladefehler).map((text) => (
          <div key={text} className="alert alert-danger" role="alert">
            {text}
          </div>
        ))}
        {page === "overview" ? (
          <Overview
            fahrzeuge={fahrzeuge}
            funkkennungen={funkkennungen}
            searchString={searchString}
            setSearchString={setSearchString}
            gotoFahrzeugPage={gotoFahrzeugPage}
          />
        ) : undefined}
        {page === "maengel" ? (
          <Maengel
            maengel={maengel}
            fahrzeuge={fahrzeuge}
            isLoading={maengelLaden}
            error={maengelFehler}
            searchString={searchString}
            gotoFahrzeugPage={gotoFahrzeugPage}
          />
        ) : undefined}
        {page === "einstellungen" ? <Einstellungen /> : undefined}
        {page === "fahrzeug" ? (
          <Fahrzeug
            fahrzeuge={fahrzeuge}
            kategorien={kategorien}
            funkkennungen={funkkennungen}
            pageKennzeichen={pageKennzeichen}
            changePage={changePage}
            setFahrzeugId={setFahrzeugId}
          />
        ) : undefined}
        {page === "protokoll" ? (
          <Protokoll
            fahrzeuge={fahrzeuge}
            funkkennungen={funkkennungen}
            searchString={searchString}
            setSearchString={setSearchString}
          />
        ) : undefined}
        {page === "protokollFahrzeug" ? (
          <ProtokollFahrzeug
            searchString={searchString}
            setSearchString={setSearchString}
            fahrzeugId={fahrzeugId}
            changePage={changePage}
            pageKennzeichen={pageKennzeichen}
          />
        ) : undefined}
        {page === "changelog" ? <Changelog /> : undefined}
      </>
    );
  }
};

const AnmeldeFehler = ({identitaet}) => {
  const abmeldenKnopf = (beschriftung = "Abmelden") => (
    <button className="btn btn-secondary" onClick={identitaet.abmelden}>
      {beschriftung}
    </button>
  );

  switch (identitaet.fehler) {
    case FEHLER_NETZ:
      return (
        <div className="login">
          <p>
            Der Anmeldeserver ist gerade nicht erreichbar. Die Anmeldung wird
            automatisch erneut versucht.
          </p>
          <button
            className="btn btn-primary"
            onClick={identitaet.erneutVersuchen}
          >
            Jetzt erneut versuchen
          </button>{" "}
          {abmeldenKnopf()}
        </div>
      );
    case FEHLER_TOKEN_URL_FEHLT:
      return (
        <div className="login">
          <p>
            Betriebsfehler: Die Anmeldung ist nicht konfiguriert, es fehlt
            REACT_APP_FIREBASE_TOKEN_URL. Bitte Administrator informieren.
          </p>
          {abmeldenKnopf()}
        </div>
      );
    case FEHLER_SITZUNG_ABGELAUFEN:
      return (
        <div className="login">
          <p>Sitzung abgelaufen, bitte erneut anmelden</p>
          {abmeldenKnopf("Erneut anmelden")}
        </div>
      );
    default:
      return (
        <div className="login">
          <p>
            Die Anmeldung konnte nicht abgeschlossen werden. Bitte Administrator
            informieren.
          </p>
          <button
            className="btn btn-primary"
            onClick={identitaet.erneutVersuchen}
          >
            Erneut versuchen
          </button>{" "}
          {abmeldenKnopf()}
        </div>
      );
  }
};

export default App;
